/**
 * Composer — chat input box + Send button. The mid-meeting composer is the
 * load-bearing mobile pattern (see /specs/PeoplePrints_Design_Chat_Kickoff_Brief.md §8).
 *
 * Visual: navy 2px border, white surface, navy-filled Send button. Quick-button
 * Chip row sits ABOVE the composer (rendered separately by the surface).
 *
 * Per locked Q5 (2026-05-02), tier-gated quick-button chips render in the
 * locked state via the Chip component; the Composer itself is tier-agnostic.
 */

import { useState } from "react";

export default function Composer({
  placeholder = "Type a message…",
  onSubmit,
  disabled = false,
}) {
  const [value, setValue] = useState("");

  const submit = () => {
    const v = value.trim();
    if (!v || disabled) return;
    if (onSubmit) onSubmit(v);
    setValue("");
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        border: "1px solid var(--ppr-color-border-default)",
        background: "var(--ppr-color-background-surface)",
        padding: "10px 12px",
        alignItems: "flex-end",
      }}
      data-component="Composer"
    >
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        rows={2}
        style={{
          flex: 1,
          resize: "none",
          border: "none",
          outline: "none",
          fontFamily: "var(--ppr-typography-font-family-ui)",
          fontSize: "var(--ppr-typography-font-size-base)",
          lineHeight: "var(--ppr-typography-line-height-normal)",
          color: "var(--ppr-color-text-primary)",
          background: "transparent",
        }}
      />
      <button
        onClick={submit}
        disabled={disabled || value.trim().length === 0}
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-sm)",
          letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
          textTransform: "uppercase",
          padding: "10px 18px",
          border: "1px solid var(--ppr-color-border-default)",
          background: "var(--ppr-color-accent-navy)",
          color: "var(--ppr-color-text-inverse)",
          cursor: disabled || value.trim().length === 0 ? "not-allowed" : "pointer",
          opacity: disabled || value.trim().length === 0 ? 0.6 : 1,
        }}
      >
        Send
      </button>
    </div>
  );
}
